import React, { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import PageHeader from "../../Components/Common/PageHeader";
import { SelectField } from "../../Components/Form/fields";
import { Button } from "../../shadcn/components/ui/button";
import { Skeleton } from "../../shadcn/components/ui/skeleton";
import { Spinner } from "../../shadcn/components/ui/spinner";
import useGraphql from "../../Hooks/useGraphql";
import {
  EDIT_ROLE_PERMISSIONS_SA,
  GET_ROLES_SA,
  GET_PERMISSIONS_SA,
} from "./SuperAdminService";
import { useTranslation } from "../../context/Internationalization";
import useLog from "../../Hooks/useLog";
import { Card, CardContent, CardHeader, CardTitle } from "../../shadcn/components/ui/card";
import { ScrollArea } from "../../shadcn/components/ui/scroll-area";
import { toast } from "sonner";

const SuperAdminRoles = () => {
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchAllPermissions();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await axiosGraphQL({ query: GET_ROLES_SA });
      setRoles(response.data.data.getRolesSA || []);
    } catch (error) {
      errorLog(error, "SuperAdminRoles", "fetchRoles", "GET_ROLES_SA");
      toast.error(error.message);
    }
    setLoading(false);
  };

  const fetchAllPermissions = async () => {
    try {
      const response = await axiosGraphQL({ query: GET_PERMISSIONS_SA });
      setAllPermissions(response.data.data.getPermissionsSA || []);
    } catch (error) {
      errorLog(
        error,
        "SuperAdminRoles",
        "fetchAllPermissions",
        "GET_PERMISSIONS_SA"
      );
      toast.error(error.message);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setSelectedPermissions(role.permissions);
  };

  const handleChange = (event) => {
    setSelectedPermissions(event.target.value);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axiosGraphQL({
        query: EDIT_ROLE_PERMISSIONS_SA,
        variables: { role: editingRole.role, permissions: selectedPermissions },
      });
      toast.success(t("permissions_updated"));
      fetchRoles();
      setEditingRole(null);
    } catch (error) {
      errorLog(
        error,
        "SuperAdminRoles",
        "handleSave",
        "EDIT_ROLE_PERMISSIONS_SA"
      );
      toast.error(error.message);
    }
    setLoading(false);
  };

  return (
    <div className="p-6">
      <PageHeader
        title={t("roles_permissions_admin")}
        actions={
          loading ? (
            <Spinner aria-label={t("loading")} className="size-5" />
          ) : null
        }
      />
      <div
        aria-busy={loading}
        className="grid grid-cols-1 gap-4 pt-5 min-[900px]:grid-cols-2"
      >
        {loading && !roles.length
          ? [0, 1].map((key) => (
              <Skeleton
                key={key}
                aria-hidden="true"
                className="h-[290px] rounded-xl"
              />
            ))
          : null}
        {roles.map((role) => (
          <Card
            key={role.role}
            role="region"
            aria-label={t(role.role.replace("-", "_"))}
            className="min-w-0"
          >
            <CardHeader className="border-b">
              <CardTitle role="heading" aria-level={2} className="text-lg">
                {t(role.role.replace("-", "_"))}
              </CardTitle>
            </CardHeader>
            <CardContent>

            {editingRole?.role === role.role ? (
              <div className="flex flex-col gap-4">
                <SelectField
                  multiple
                  label={t("permissions")}
                  name="permissions"
                  value={selectedPermissions}
                  onChange={handleChange}
                  options={allPermissions.map((permission) => ({
                    value: permission.name,
                    text: permission.description,
                  }))}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingRole(null)}
                  >
                    {t("close")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {t("save")}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <ScrollArea className="h-[200px] rounded-md border p-2">
                  <p className="whitespace-pre-line">
                    {allPermissions
                      .filter((permission) =>
                        role.permissions.includes(permission.name)
                      )
                      .map((permission) => permission.description)
                      .join("\n")}
                  </p>
                </ScrollArea>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  disabled={loading}
                  onClick={() => handleEdit(role)}
                >
                  <Pencil aria-hidden="true" />
                  {t("edit")}
                </Button>
              </>
            )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SuperAdminRoles;
